// "Já li os alertas" — a marca de leitura da fila da Vick.
//
// Fica no navegador, por usuário, NÃO no banco. `ia_alertas` é uma tabela
// compartilhada: quem resolve baixa o alerta para todo mundo, e é assim que
// tem que ser. Leitura é o contrário — é de cada pessoa, e gravar isso em
// coluna transformaria "eu já vi" em "ninguém mais precisa ver".
//
// Por isso também não é a mesma coisa que "resolvido": ler não conserta nada.
// Serve para o contador vermelho parar de gritar sobre um alerta que a pessoa
// já leu e decidiu deixar para depois.
//
// Some ao limpar o navegador ou ao trocar de máquina. É aceitável: no pior
// caso a fila volta a parecer nova, que é o estado seguro.

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

/** Chave por usuário: duas pessoas na mesma máquina não herdam a leitura da outra. */
const chaveDe = (userId: string | null | undefined) =>
  `vick:alertas-lidos-ate:${userId ?? 'anon'}`;

/** localStorage não avisa a própria aba; este evento faz o papel. */
const EVENTO = 'vigorhub:vick-alertas-lidos';

const ler = (userId: string | null | undefined): number => {
  try {
    const v = localStorage.getItem(chaveDe(userId));
    const n = v ? Date.parse(v) : NaN;
    return Number.isFinite(n) ? n : 0;
  } catch {
    // Modo privado / storage bloqueado: tudo conta como não lido.
    return 0;
  }
};

export interface AlertasLidos {
  /** Epoch ms da última leitura. 0 = nunca leu (tudo é novo). */
  lidosAte: number;
  /** Marca tudo que já existe como lido. */
  marcarLidos: () => void;
}

export function useAlertasLidos(): AlertasLidos {
  const { user } = useAuth();
  const userId = user?.id;
  const [lidosAte, setLidosAte] = useState(() => ler(userId));

  // Troca de usuário sem desmontar (login/logout na mesma aba): recalcula em
  // render, não em efeito. Efeito só para reagir à mudança daria um quadro com
  // a leitura do usuário anterior na tela.
  const [donoDaLeitura, setDonoDaLeitura] = useState(userId);
  if (userId !== donoDaLeitura) {
    setDonoDaLeitura(userId);
    setLidosAte(ler(userId));
  }

  // Painel flutuante e tela cheia podem estar montados ao mesmo tempo (o painel
  // abre por cima de /vick). Sem o evento, marcar num lugar deixaria o contador
  // do outro parado.
  useEffect(() => {
    const sincronizar = () => setLidosAte(ler(userId));
    window.addEventListener(EVENTO, sincronizar);
    window.addEventListener('storage', sincronizar);
    return () => {
      window.removeEventListener(EVENTO, sincronizar);
      window.removeEventListener('storage', sincronizar);
    };
  }, [userId]);

  const marcarLidos = useCallback(() => {
    const agora = Date.now();
    try {
      localStorage.setItem(chaveDe(userId), new Date(agora).toISOString());
    } catch {
      // Sem storage a marca vale só para esta sessão — melhor que quebrar.
    }
    setLidosAte(agora);
    window.dispatchEvent(new Event(EVENTO));
  }, [userId]);

  return { lidosAte, marcarLidos };
}

/** Um alerta é novo se apareceu depois da última leitura desta pessoa. */
export const alertaNaoLido = (detectadoEm: string, lidosAte: number): boolean =>
  Date.parse(detectadoEm) > lidosAte;
